/**
 * The share card a link previews with.
 *
 * Why a helper rather than a default in root.tsx
 * ----------------------------------------------
 * Remix v2 does not merge meta: the deepest matching route's `meta` export
 * replaces its ancestors' entirely. A default declared once in root would
 * therefore reach only the routes that export no meta of their own — and putting
 * raw tags in root's JSX <head> instead would emit a second og:image on every
 * route that already sets one, which is the duplicate-tag fault we just removed
 * from the robots meta. So each route spreads this in, and there is exactly one
 * of each tag per page.
 *
 * Why width and height are conditional
 * ------------------------------------
 * Every caller used to declare 1200x630 whatever the file was. Crawlers lay the
 * preview out from those numbers before they have fetched the image, so a wrong
 * pair is worse than none — the sitewide fallback it was claiming for is
 * 960x350. The dimensions are published only for the default card, whose size we
 * control; a CMS image's size is not knowable here, so it goes without.
 */

/** 1200x630. Built from the Catalyst wordmark and the painted triangle mark. */
export const DEFAULT_OG_IMAGE = 'https://catalyst-cannabis.com/images/catalyst-og-default.png';

export const DEFAULT_OG_IMAGE_ALT =
  'Catalyst Cannabis — California cannabis dispensaries';

interface SocialImageOptions {
  /** A page-specific image. Falls back to the default card when absent. */
  image?: string | null;
  /** Describe the image, not the page — screen readers read this out. */
  alt?: string;
}

export function socialImageMeta({ image, alt }: SocialImageOptions = {}): Record<string, string>[] {
  const specific = image?.trim();

  if (specific) {
    return [
      { property: 'og:image', content: specific },
      { property: 'og:image:alt', content: alt || DEFAULT_OG_IMAGE_ALT },
      { name: 'twitter:image', content: specific },
    ];
  }

  return [
    { property: 'og:image', content: DEFAULT_OG_IMAGE },
    { property: 'og:image:type', content: 'image/png' },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'og:image:alt', content: alt || DEFAULT_OG_IMAGE_ALT },
    { name: 'twitter:image', content: DEFAULT_OG_IMAGE },
  ];
}
